<template>
  <div class="install-guide">
    <!-- 浏览器切换标签 -->
    <div class="guide-header">
      <div class="browser-tabs">
        <div
          v-for="browser in browsers"
          :key="browser.key"
	          class="browser-tab"
	          :class="{ active: currentBrowser === browser.key }"
	          :data-track-text="`查看${browser.name}安装引导`"
	          :aria-label="`查看${browser.name}安装引导`"
	          @click="selectBrowser(browser.key)"
        >
          <img :src="browser.icon" class="browser-icon" alt="" />
          <span>{{ browser.name }}</span>
        </div>
      </div>
    </div>

    <!-- 主内容区：左侧步骤 + 右侧图片 -->
    <div class="guide-body">
      <!-- 左侧步骤列表 -->
      <div class="steps-panel">
        <div
          v-for="(step, index) in currentSteps"
          :key="index"
	          class="step-item"
	          :class="{ active: activeStep === index }"
	          :data-track-text="`查看安装步骤${index + 1}`"
	          :aria-label="`查看安装步骤${index + 1}`"
	          @click="activeStep = index"
        >
          <div class="step-number">{{ index + 1 }}</div>
          <div class="step-info">
            <div class="step-title">{{ step.title }}</div>
            <div class="step-desc" v-html="step.desc"></div>
          </div>
        </div>
      </div>

      <!-- 右侧图片展示 -->
      <div class="image-panel">
	        <div class="image-wrapper" data-track-text="打开安装预览" aria-label="打开安装预览" @click="openPreview">
          <img
            :src="currentSteps[activeStep]?.image || placeholderImage"
            :alt="currentSteps[activeStep]?.title"
            class="guide-image"
          />
          <div class="zoom-hint">
            <i class="el-icon-zoom-in"></i>
            <span>点击放大</span>
          </div>
          <div class="image-caption">
            <span class="caption-step">步骤 {{ activeStep + 1 }}</span>
            <span class="caption-title">{{ currentSteps[activeStep]?.title }}</span>
          </div>
        </div>
      </div>
    </div>

    <!-- 底部提示 -->
    <div class="guide-footer">
      <div class="tip">
        <i class="el-icon-info"></i>
        <span>安装完成后，请刷新当前页面以激活插件功能</span>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <div v-if="showPreview" class="preview-modal" data-track-text="关闭安装预览" aria-label="关闭安装预览" @click.self="closePreview">
      <!-- 左箭头 -->
      <div
        class="preview-arrow prev"
        data-track-text="上一张安装预览"
        aria-label="上一张安装预览"
        :class="{ disabled: activeStep === 0 }"
        @click="prevImage"
      >
        <i class="el-icon-arrow-left"></i>
      </div>

      <!-- 预览内容 -->
      <div class="preview-content">
        <img
          :src="currentSteps[activeStep]?.image || placeholderImage"
          :alt="currentSteps[activeStep]?.title"
          class="preview-image"
        />
        <div class="preview-info">
          <span class="preview-step">步骤 {{ activeStep + 1 }} / {{ currentSteps.length }}</span>
          <span class="preview-title">{{ currentSteps[activeStep]?.title }}</span>
        </div>
      </div>

      <!-- 右箭头 -->
      <div
        class="preview-arrow next"
        data-track-text="下一张安装预览"
        aria-label="下一张安装预览"
        :class="{ disabled: activeStep === currentSteps.length - 1 }"
        @click="nextImage"
      >
        <i class="el-icon-arrow-right"></i>
      </div>

      <!-- 关闭按钮 -->
      <div class="preview-close" data-track-text="关闭预览" aria-label="关闭预览" @click="closePreview">
        <i class="el-icon-close"></i>
      </div>

      <!-- 缩略图导航 -->
      <div class="preview-thumbnails">
        <div
          v-for="(step, index) in currentSteps"
          :key="index"
	          class="thumbnail-item"
	          :class="{ active: activeStep === index }"
	          :data-track-text="`选择安装预览${index + 1}`"
	          :aria-label="`选择安装预览${index + 1}`"
	          @click="activeStep = index"
        >
          <img :src="step.image || placeholderImage" :alt="step.title" />
          <span class="thumbnail-num">{{ index + 1 }}</span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'InstallGuide',
  props: {
    defaultBrowser: {
      type: String,
      default: 'chrome'
    }
  },
  data() {
    return {
      currentBrowser: this.defaultBrowser,
      activeStep: 0,
      showPreview: false,
      // 占位图片
      placeholderImage: 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAwIiBoZWlnaHQ9IjMwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZjFmNWY5Ii8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtZmFtaWx5PSJBcmlhbCIgZm9udC1zaXplPSIxNiIgZmlsbD0iIzk0YTNiOCIgdGV4dC1hbmNob3I9Im1pZGRsZSIgZHk9Ii4zZW0iPuWbvueJh+aMh+WvvOWNoOS9jTwvdGV4dD48L3N2Zz4=',
      browsers: [
        {
          key: 'chrome',
          name: 'Chrome',
          icon: require('@/assets/chrome.png')
        },
        {
          key: 'edge',
          name: 'Edge',
          icon: require('@/assets/edge.png')
        },
        {
          key: '360',
          name: '360',
          icon: require('@/assets/360.png')
        },
        {
          key: 'firefox',
          name: 'Firefox',
          icon: require('@/assets/firefox.png')
        }
      ],
      // 各浏览器安装步骤
      stepsData: {
        chrome: [
          {
            title: '下载插件',
            desc: '点击上方按钮下载 <b>.crx</b> 格式的插件安装包',
            image: require('@/assets/google_step_1.jpg')
          },
          {
            title: '打开扩展程序页面',
            desc: '在地址栏输入 <code>chrome://extensions</code> 并回车<br/>或点击菜单 → 更多工具 → 扩展程序',
            image: require('@/assets/google_step_2.jpg')
          },
          {
            title: '开启开发者模式',
            desc: '在扩展程序页面<b>右上角</b>，打开「开发者模式」开关',
            image: require('@/assets/google_step_3.jpg')
          },
          {
            title: '安装插件',
            desc: '将下载的 <b>.crx</b> 文件直接拖入扩展程序页面即可完成安装',
            image: require('@/assets/google_step_4.jpg')
          },
          {
            title: '安装完成',
            desc: '安装成功后，点击浏览器右上角 <b>扩展图标</b>，将插件 <b>固定</b> 到地址栏方便使用',
            image: require('@/assets/google_step_5.jpg')
          }
        ],
        edge: [
          {
            title: '下载插件',
            desc: '点击上方按钮下载 <b>.crx</b> 格式的插件安装包',
            image: require('@/assets/edge_step_1.jpg')
          },
          {
            title: '打开扩展程序页面',
            desc: '在地址栏输入 <code>edge://extensions</code> 并回车<br/>或点击菜单 → 扩展 → 管理扩展',
            image: require('@/assets/edge_step_2.jpg')
          },
          {
            title: '开启开发人员模式',
            desc: '在扩展程序页面<b>左下角</b>，打开「开发人员模式」开关',
            image: require('@/assets/edge_step_3.png')
          },
          {
            title: '安装插件',
            desc: '将下载的 <b>.crx</b> 文件直接拖入扩展程序页面即可完成安装',
            image: require('@/assets/edge_step_4.png')
          },
          {
            title: '安装完成',
            desc: '安装成功后，点击浏览器右上角 <b>扩展图标</b>，将插件 <b>固定</b> 到地址栏方便使用',
            image: require('@/assets/edge_step_5.png')
          }
        ],
        '360': [
          {
            title: '下载插件',
            desc: '点击上方按钮下载 <b>.crx</b> 格式的插件安装包',
            image: require('@/assets/360_step_1.png')
          },
          {
            title: '打开扩展管理',
            desc: '在地址栏输入 <code>chrome://extensions</code> 并回车<br/>或点击浏览器右上角 <b>三横线菜单</b><br/>选择 <b>设置</b> → <b>扩展程序</b>',
            image: require('@/assets/360_step_2.png')
          },
          {
            title: '开启开发者模式',
            desc: '在扩展管理页面<b>右上角</b>，打开「开发者模式」开关',
            image: require('@/assets/360_step_3.png')
          },
          {
            title: '安装插件',
            desc: '将下载的 <b>.crx</b> 文件直接拖入扩展管理页面即可完成安装',
            image: require('@/assets/360_step_4.png')
          },
          {
            title: '安装完成',
            desc: '安装成功后，点击浏览器右上角 <b>扩展图标</b>，将插件 <b>固定</b> 到地址栏方便使用',
            image:  require('@/assets/360_step_5.png')
          }
        ],
        firefox: [
          {
            title: '下载插件',
            desc: '点击上方按钮下载 <b>.xpi</b> 格式的插件安装包',
            image: require('@/assets/firefox_step_1.jpg')
          },
          {
            title: '打开附加组件页面',
            desc: '在地址栏输入 <code>about:addons</code> 并回车<br/>或按 <code>Ctrl+Shift+A</code> 快捷键',
            image: require('@/assets/firefox_step_2.jpg')
          },
          {
            title: '从文件安装',
            desc: '点击右上角 <b>设置图标（齿轮）</b>，选择「从文件安装附加组件」',
            image: require('@/assets/firefox_step_3.jpg')
          },
          {
            title: '选择文件完成安装',
            desc: '选择下载的 <b>.xpi</b> 文件，点击确认即可完成安装',
            image: require('@/assets/firefox_step_4.jpg')
          },
          {
            title: '安装完成',
            desc: '安装成功后，点击浏览器右上角 <b>扩展图标</b>，将插件 <b>固定</b> 到工具栏方便使用',
            image: require('@/assets/firefox_step_5.jpg')
          }
        ]
      }
    };
  },
  computed: {
    currentSteps() {
      return this.stepsData[this.currentBrowser] || this.stepsData.chrome;
    }
  },
  watch: {
    defaultBrowser(val) {
      this.currentBrowser = val;
      this.activeStep = 0;
    }
  },
  mounted() {
    this.detectBrowser();
    document.addEventListener('keydown', this.handleKeydown);
  },
  beforeDestroy() {
    document.removeEventListener('keydown', this.handleKeydown);
  },
  methods: {
    selectBrowser(key) {
      this.currentBrowser = key;
      this.activeStep = 0;
    },
    /** 检测是否为360浏览器 */
    is360Browser() {
      const ua = navigator.userAgent.toLowerCase();
      // 方法1: 直接检测UA中是否包含360标识
      if (ua.includes('360') || ua.includes('qihu')) {
        return true;
      }
      // 方法2: 使用现代userAgentData API检测
      if (navigator.userAgentData && navigator.userAgentData.brands) {
        const brands = navigator.userAgentData.brands.map(b => b.brand.toLowerCase());
        if (brands.some(b => b.includes('360') || b.includes('qihu'))) {
          return true;
        }
      }
      // 方法3: 检测360浏览器窗口特征 (360浏览器侧边栏导致宽度差异较大)
      if (ua.includes('chrome') && !ua.includes('edg') && !ua.includes('opr')) {
        // 360浏览器通常有较宽的边框
        const widthDiff = window.outerWidth - window.innerWidth;
        const heightDiff = window.outerHeight - window.innerHeight;
        if (widthDiff > 180 || heightDiff > 150) {
          // 配合中文语言环境判断
          const lang = navigator.language || '';
          if (lang.startsWith('zh')) {
            return true;
          }
        }
      }
      return false;
    },
    detectBrowser() {
      const ua = navigator.userAgent.toLowerCase();
      // 360浏览器检测(需要在Chrome检测之前,因为360也包含chrome标识)
      if (this.is360Browser()) {
        this.currentBrowser = '360';
      } else if (ua.includes('edg')) {
        this.currentBrowser = 'edge';
      } else if (ua.includes('chrome')) {
        this.currentBrowser = 'chrome';
      } else if (ua.includes('firefox')) {
        this.currentBrowser = 'firefox';
      }
    },
    openPreview() {
      this.showPreview = true;
      document.body.style.overflow = 'hidden';
    },
    closePreview() {
      this.showPreview = false;
      document.body.style.overflow = '';
    },
    prevImage() {
      if (this.activeStep > 0) {
        this.activeStep--;
      }
    },
    nextImage() {
      if (this.activeStep < this.currentSteps.length - 1) {
        this.activeStep++;
      }
    },
    handleKeydown(e) {
      if (!this.showPreview) return;
      if (e.key === 'Escape') {
        this.closePreview();
      } else if (e.key === 'ArrowLeft') {
        this.prevImage();
      } else if (e.key === 'ArrowRight') {
        this.nextImage();
      }
    }
  }
};
</script>

<style scoped>
.install-guide {
  width: 100%;
  max-width: 1000px;
  margin: 0 auto;
  padding: 0 20px;
  background: #fff;
  border-radius: 12px;
  box-sizing: border-box;
}

/* 浏览器标签 */
.guide-header {
  margin-bottom: 24px;
}

.browser-tabs {
  display: flex;
  justify-content: center;
  gap: 12px;
  flex-wrap: wrap;
}

.browser-tab {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  border-radius: 8px;
  cursor: pointer;
  background: #f5f7fa;
  transition: all 0.2s ease;
  font-size: 14px;
  color: #606266;
}

.browser-tab:hover {
  background: #e0f4fc;
  color: var(--primary);
}

.browser-tab.active {
  background: var(--primary);
  color: #fff;
}

.browser-icon {
  width: 24px;
  height: 24px;
  border-radius: 4px;
}

/* 主内容区 */
.guide-body {
  display: flex;
  gap: 24px;
  min-height: 320px;
}

/* 左侧步骤面板 */
.steps-panel {
  flex: 0 0 360px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.step-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: 2px solid transparent;
}

.step-item:hover {
  background: #f1f5f9;
}

.step-item.active {
  background: #e0f4fc;
  border-color: var(--primary);
}

.step-number {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  background: #e2e8f0;
  color: #64748b;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.2s ease;
}

.step-item.active .step-number {
  background: var(--primary);
  color: #fff;
}

.step-info {
  flex: 1;
  min-width: 0;
}

.step-title {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 4px;
}

.step-item.active .step-title {
  color: var(--primary);
}

.step-desc {
  font-size: 13px;
  color: #64748b;
  line-height: 1.5;
}

.step-desc >>> code {
  background: #e0f4fc;
  color: var(--primary);
  padding: 1px 4px;
  border-radius: 3px;
  font-family: monospace;
  font-size: 12px;
}

.step-desc >>> b {
  color: var(--primary);
  font-weight: 600;
}

/* 右侧图片面板 */
.image-panel {
  flex: 1;
  display: flex;
  align-items: stretch;
}

.image-wrapper {
  width: 100%;
  background: #f8fafc;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e2e8f0;
  cursor: pointer;
  position: relative;
  display: flex;
  flex-direction: column;
}

.image-wrapper:hover .zoom-hint {
  opacity: 1;
}

.guide-image {
  width: 100%;
  flex: 1;
  object-fit: cover;
  display: block;
  background: #f1f5f9;
  min-height: 280px;
}

.zoom-hint {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: rgba(0, 0, 0, 0.6);
  color: #fff;
  padding: 12px 20px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  opacity: 0;
  transition: opacity 0.2s ease;
  pointer-events: none;
}

.zoom-hint i {
  font-size: 18px;
}

.image-caption {
  padding: 12px 16px;
  background: #fff;
  border-top: 1px solid #e2e8f0;
  display: flex;
  align-items: center;
  gap: 12px;
}

.caption-step {
  background: var(--primary);
  color: #fff;
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 600;
}

.caption-title {
  font-size: 14px;
  color: #1e293b;
  font-weight: 500;
}

/* 底部提示 */
.guide-footer {
  margin-top: 20px;
  padding-top: 16px;
  border-top: 1px solid #e2e8f0;
}

.tip {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  color: #64748b;
  font-size: 13px;
}

.tip i {
  color: var(--primary);
}

/* 图片预览弹窗 */
.preview-modal {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.9);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 60px 80px 100px;
}

.preview-content {
  max-width: 90%;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.preview-image {
  max-width: 100%;
  max-height: calc(100vh - 200px);
  object-fit: contain;
  border-radius: 8px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.5);
}

.preview-info {
  margin-top: 16px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.preview-step {
  background: var(--primary);
  color: #fff;
  padding: 6px 14px;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
}

.preview-title {
  color: #fff;
  font-size: 16px;
  font-weight: 500;
}

/* 左右箭头 */
.preview-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 50px;
  height: 50px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #fff;
  font-size: 24px;
}

.preview-arrow:hover {
  background: rgba(255, 255, 255, 0.2);
}

.preview-arrow.disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.preview-arrow.prev {
  left: 20px;
}

.preview-arrow.next {
  right: 20px;
}

/* 关闭按钮 */
.preview-close {
  position: absolute;
  top: 20px;
  right: 20px;
  width: 44px;
  height: 44px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  color: #fff;
  font-size: 20px;
}

.preview-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

/* 缩略图导航 */
.preview-thumbnails {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 10px;
  background: rgba(0, 0, 0, 0.5);
  padding: 10px 16px;
  border-radius: 10px;
}

.thumbnail-item {
  width: 60px;
  height: 45px;
  border-radius: 6px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  border: 2px solid transparent;
  transition: all 0.2s ease;
  opacity: 0.6;
}

.thumbnail-item:hover {
  opacity: 0.9;
}

.thumbnail-item.active {
  border-color: var(--primary);
  opacity: 1;
}

.thumbnail-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.thumbnail-num {
  position: absolute;
  bottom: 2px;
  right: 2px;
  background: rgba(0, 0, 0, 0.7);
  color: #fff;
  font-size: 10px;
  padding: 1px 5px;
  border-radius: 3px;
}

/* 响应式 */
@media (max-width: 768px) {
  .install-guide {
    min-width: 0;
    padding: 20px 0;
  }

  .guide-header {
    margin-bottom: 16px;
  }

  .browser-tabs {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 8px;
  }

  .browser-tab {
    justify-content: center;
    gap: 6px;
    min-width: 0;
    min-height: 44px;
    padding: 10px 8px;
    box-sizing: border-box;
  }

  .browser-tab span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .browser-icon {
    flex: 0 0 22px;
    width: 22px;
    height: 22px;
  }

  .guide-body {
    flex-direction: column;
    gap: 16px;
    min-width: 0;
    min-height: 0;
  }

  .steps-panel {
    flex: none;
    order: -1;
    flex-direction: row;
    gap: 10px;
    width: 100%;
    min-width: 0;
    padding-bottom: 4px;
    overflow-x: auto;
    overflow-y: hidden;
    scroll-snap-type: x proximity;
    overscroll-behavior-x: contain;
    scrollbar-width: thin;
    scrollbar-color: #cbd5e1 transparent;
  }

  .steps-panel::-webkit-scrollbar {
    height: 4px;
  }

  .steps-panel::-webkit-scrollbar-thumb {
    border-radius: 999px;
    background: #cbd5e1;
  }

  .image-panel {
    order: 0;
    min-width: 0;
  }

  .step-item {
    flex: 0 0 260px;
    gap: 10px;
    min-width: 0;
    min-height: 92px;
    padding: 12px;
    box-sizing: border-box;
    scroll-snap-align: start;
  }

  .step-desc {
    overflow-wrap: anywhere;
  }

  .image-wrapper {
    min-width: 0;
  }

  .guide-image {
    flex: 0 0 auto;
    width: 100%;
    height: auto;
    min-height: 0;
    aspect-ratio: 16 / 9;
    object-fit: contain;
  }

  .image-caption {
    flex-wrap: wrap;
    gap: 8px;
    padding: 10px 12px;
  }

  .guide-footer {
    margin-top: 16px;
    padding-top: 12px;
  }

  .tip {
    align-items: flex-start;
    text-align: left;
  }

  .preview-modal {
    height: 100vh;
    height: 100dvh;
    padding: calc(52px + env(safe-area-inset-top)) 16px calc(82px + env(safe-area-inset-bottom));
    box-sizing: border-box;
  }

  .preview-content {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    max-height: 100%;
  }

  .preview-image {
    max-height: calc(100vh - 180px);
    max-height: calc(100dvh - 180px);
  }

  .preview-arrow {
    width: 44px;
    height: 44px;
    font-size: 20px;
  }

  .preview-arrow.prev {
    left: 10px;
  }

  .preview-arrow.next {
    right: 10px;
  }

  .preview-close {
    top: calc(10px + env(safe-area-inset-top));
    right: 10px;
    width: 44px;
    height: 44px;
  }

  .preview-thumbnails {
    right: 12px;
    bottom: calc(10px + env(safe-area-inset-bottom));
    left: 12px;
    width: auto;
    max-width: none;
    padding: 8px;
    overflow-x: auto;
    overflow-y: hidden;
    box-sizing: border-box;
    transform: none;
    justify-content: flex-start;
    overscroll-behavior-x: contain;
  }

  .thumbnail-item {
    flex: 0 0 50px;
    width: 50px;
    height: 38px;
  }
}

@media (max-width: 480px) {
  .browser-tabs {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .browser-tab {
    font-size: 13px;
  }

  .step-item {
    flex-basis: calc(100% - 64px);
  }

  .preview-modal {
    padding-right: 12px;
    padding-left: 12px;
  }

  .thumbnail-item {
    flex-basis: 46px;
    width: 46px;
    height: 35px;
  }
}
</style>
